import { useTranslation } from 'react-i18next';
import numeral from 'numeral';

import { compressorTypes, DeviceType } from '@/constants/deviceTypeEnum';
import { useMeasurement } from '@/hooks/useMeasurement';
import { useValueByConnectivity } from '@/hooks/useValueByConnectivity';
import { useStores } from '@/hooks/use-stores';

import { useLabelValueItemsByDeviceType } from '../../DetailsDrawer/Tabs/AssetInfo/AssetInfo.utils';
import { useLabelValueRealtimeItemsByDeviceType } from '../../DetailsDrawer/Tabs/AssetInfo/RealtimeData/RealtimeData.utils';
import { useLabelValueServiceInformationItemsByDeviceType } from '../../DetailsDrawer/Tabs/AssetInfo/ServiceInfo/ServiceInfo.utils';
import { useDewPointValue } from '../../../desiccantAirDryer/DessicantMapView/DesiccantMapView.utils';
import { MetricType } from '@/constants/metric-type.enum';

export const useMinimizedViewLabelValueItemsByDeviceType = entity => {
  const {
    deviceType,
    pressureBar,
    inletValveOpening,
    bypassValveOpening,
    minimumPressureBar,
    maximumPressureBar,
    dewpointTemperatureCelsius,
    spillValveHpPressureBar,
    spillValveLpPressureBar,
    spillValveHpLowLimitSetpointBar,
    spillValveLpTargetSetpointBar,
    connectivity,
  } = entity;
  const { t } = useTranslation();
  const { tvalue, tunit } = useMeasurement();
  const { tcvalue } = useValueByConnectivity();
  const { stationStore } = useStores();

  const onDevices = stationStore.currentStation?.devices?.filter(device => entity.onDevices.includes(device.deviceId));
  const dewPoint = useDewPointValue(dewpointTemperatureCelsius, onDevices);

  const labelValueItems = [];
  const generalLabelValueItems = useLabelValueItemsByDeviceType(entity);
  const realtimeLabelValueItems = useLabelValueRealtimeItemsByDeviceType(entity);
  const serviceLabelValueItems = useLabelValueServiceInformationItemsByDeviceType(entity);

  if (compressorTypes.includes(deviceType as DeviceType) || deviceType === DeviceType.Booster) {
    const minTValue = tvalue(Number(minimumPressureBar?.toFixed(2)), 'bar');
    const maxTValue = tvalue(Number(maximumPressureBar?.toFixed(2)), 'bar');

    realtimeLabelValueItems.unshift(
      deviceType === DeviceType.Blower
        ? {
            label: t('blowerSpeed'),
            value: tvalue(entity.blowerSpeedRPM, 'RPM'),
            unit: t('%'),
          }
        : {
            label: t('pressure'),
            value: tvalue(Number(pressureBar?.toFixed(2)), 'bar'),
            unit: t(tunit('Bar')),
            suffix: `(min ${tcvalue(minTValue, connectivity)}, max ${tcvalue(maxTValue, connectivity)})`,
          },
      ...([DeviceType.Centrifugal, DeviceType.CentrifugalAndPowerSensor, DeviceType.Blower].includes(
        deviceType as DeviceType,
      )
        ? [
            {
              label: t('inletValve'),
              value: numeral(inletValveOpening).format(0),
              unit: '%',
            },
            {
              label: t('bypassValve'),
              value: numeral(bypassValveOpening).format(0),
              unit: '%',
            },
          ]
        : []),
    );
  } else if (deviceType === DeviceType.DesiccantAirDryer) {
    realtimeLabelValueItems.unshift({
      label: t('dew point'),
      value: dewPoint,
      unit: t(tunit('°C')),
    });
  } else if (deviceType === DeviceType.SpillValve) {
    const lowLimit = tvalue(Number((spillValveHpLowLimitSetpointBar * 0.99)?.toFixed(2)), 'bar');
    const target = tvalue(Number((spillValveLpTargetSetpointBar * 0.99)?.toFixed(2)), 'bar');
    realtimeLabelValueItems.unshift(
      {
        label: `${t('HP')} ${t('pressureSetpoints')}`,
        value: tvalue(Number(spillValveHpPressureBar?.toFixed(2)), 'bar'),
        unit: t(tunit('Bar')),
        suffix: `(min ${tcvalue(lowLimit, connectivity)})`,
      },
      {
        label: `${t('LP')} ${t('pressureSetpoints')}`,
        value: tvalue(Number(spillValveLpPressureBar?.toFixed(2)), 'bar'),
        unit: t(tunit('Bar')),
        suffix: `(max ${tcvalue(target, connectivity)})`,
      },
    );
  } else if (deviceType === DeviceType.AerationBasin) {
    realtimeLabelValueItems.push({
      label: t('dissolvedOxygen'),
      value: tvalue(entity[MetricType.DissolvedOxygenPPM], 'ppm'),
      unit: t(tunit('ppm')),
    });
  }

  labelValueItems.push(...generalLabelValueItems, ...realtimeLabelValueItems, ...serviceLabelValueItems);
  return labelValueItems;
};
